import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HamburgerMenu from 'react-hamburger-menu';
import './Home.css';
import hvacShopBg from '../../assets/hvacshop.webp';
import socialProofing from '../../assets/social-proofing.webp';
import hvacShopBgMobile from '../../assets/hvacshop-mobile.webp';

import jobs from '../../assets/jobs.webp';
import kanbanboard from '../../assets/kanbanboard.webp';
import loadcalc from '../../assets/loadcalc.webp';
import invoices from '../../assets/invoicecreation.webp';
import leadsfunnel from '../../assets/leadsfunnel.webp';
import customerdetails from '../../assets/customerdetails.webp';
import roombyroomloadcalc from '../../assets/roombyroomloadcalc.webp';
import pricebook from '../../assets/pricebook.webp';
import pricebookedit from '../../assets/pricebookedit.webp';
import website from '../../assets/website.webp';
import chatbot from '../../assets/chatbot.webp';
import techspage from '../../assets/techs.webp';

import Dialog from '@mui/material/Dialog';
import Header from '../Layout/Header';
import Footer from '../Layout/Footer';

const Home = () => {
  const navigate = useNavigate();
  const [isHeroImageLoaded, setIsHeroImageLoaded] = useState(false);

  useEffect(() => {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        'Complete home services business management platform for HVAC, plumbing, and electrical contractors. ' +
        'Features include smart invoicing, scheduling, customer management, and specialized service tools. ' +
        'Try our demo to streamline your operations today.'
      );
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Complete home services business management platform for HVAC, plumbing, and electrical contractors. ' +
        'Features include smart invoicing, scheduling, customer management, and specialized service tools. ' +
        'Try our demo to streamline your operations today.';
      document.head.appendChild(meta);
    }

    document.title = 'Fixetra - Complete Home Services Business Management Platform';

    return () => {
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', '');
      }
    };
  }, []);

  useEffect(() => {
    // Add Google Analytics script
    const gaScript1 = document.createElement('script');
    gaScript1.async = true;
    gaScript1.src = 'https://www.googletagmanager.com/gtag/js?id=G-ZGVM2TPDW1';
    
    const gaScript2 = document.createElement('script');
    gaScript2.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-ZGVM2TPDW1');
    `;
    
    document.head.appendChild(gaScript1);
    document.head.appendChild(gaScript2);

    return () => {
      // Cleanup
      document.head.removeChild(gaScript1);
      document.head.removeChild(gaScript2);
    };
  }, []);

  useEffect(() => {
    // Preload hero background image with priority
    const preloadImage = () => {
      const img = new Image();
      
      // Choose appropriate image based on viewport
      const isMobile = window.innerWidth <= 768;
      const imageUrl = isMobile ? hvacShopBgMobile : hvacShopBg;
      
      img.fetchPriority = 'high';
      img.loading = 'eager';
      
      img.onload = () => {
        setIsHeroImageLoaded(true);
        const heroBackground = document.querySelector('.hero-background');
        if (heroBackground) {
          heroBackground.style.backgroundImage = `url(${imageUrl})`;
        }
      };

      img.onerror = () => {
        console.error('Failed to load hero image');
      };

      img.src = imageUrl;
    };

    preloadImage();
    
    // Add resize listener for responsive images
    const handleResize = () => {
      preloadImage();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle scroll animations
  useEffect(() => {
    const animateOnScroll = () => {
      // Get all feature and testimonial cards
      const featureCards = document.querySelectorAll('.feature-card');
      const testimonialCards = document.querySelectorAll('.testimonial-card');
      
      // Function to check if element is in viewport
      const isInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        return (
          rect.top <= (window.innerHeight || document.documentElement.clientHeight) * 0.85
        );
      };
      
      // Animate feature cards when they come into view
      featureCards.forEach(card => {
        if (isInViewport(card) && !card.classList.contains('animate')) {
          card.classList.add('animate');
        }
      });
      
      // Animate testimonial cards when they come into view
      testimonialCards.forEach((card, index) => {
        if (isInViewport(card) && !card.classList.contains('animate')) {
          // Add a staggered delay for each card
          setTimeout(() => {
            card.classList.add('animate');
          }, index * 150);
        }
      });
    };
    
    // Run once when component mounts to animate already visible elements
    animateOnScroll();
    
    // Add event listener for scroll
    window.addEventListener('scroll', animateOnScroll);
    
    // Clean up event listener
    return () => window.removeEventListener('scroll', animateOnScroll);
  }, []);

  // Feature definitions - marketing-oriented case studies
  const allFeatures = [
    {
      id: 'blog',
      title: 'Modern Discussion Forum Increases Customer Engagement by 65%',
      cta: 'Explore Our Blog',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fwebsite-card.jpg?alt=media',
      description: 'See how our modern Hacker News style blog with cascading comments helps businesses build community and increase customer engagement.',
      price: '19.99'
    },
    {
      id: 'pricebook',
      title: 'How This HVAC Company Increased Profits by 32% in Just 60 Days',
      cta: 'See Their Pricing System',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fpricebook-card.jpg?alt=media',
      description: 'Learn how a professional HVAC company used our pricebook system to standardize pricing and increase profits by 32% in just 60 days.',
      price: '19.99'
    },
    {
      id: 'scheduling',
      title: 'The 3-Step Process That Added 4 Extra Service Calls Per Day',
      cta: 'Discover Their Schedule',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fscheduling-card.jpg?alt=media',
      description: 'Discover the simple 3-step scheduling process that allowed technicians to complete 4 additional service calls daily, boosting revenue.',
      price: '24.99'
    },
    {
      id: 'manualJ',
      title: 'Why 87% of HVAC Contractors Get Load Calculations Wrong (And How to Fix It)',
      cta: 'Try Our Calculator',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fmanualj-card.jpg?alt=media',
      description: 'Learn the common mistakes in HVAC load calculations that 87% of contractors make, and how our Manual J calculator provides accurate results.',
      price: '29.99'
    },
    {
      id: 'plumbingCalc',
      title: 'The Hidden Calculation That Saved This Plumber From a $45,000 Lawsuit',
      cta: 'Check the Calculator',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fplumbing-card.jpg?alt=media',
      description: 'See how our plumbing calculator helped prevent a costly $45,000 lawsuit by providing accurate pipe sizing and flow rate calculations.',
      price: '24.99'
    },
    {
      id: 'inspections',
      title: 'How Digital Inspections Helped Triple This Company\'s Upsell Conversion Rate',
      cta: 'See Their System',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Finspections-card.jpg?alt=media',
      description: 'Discover how transitioning to digital inspections helped this service business triple their upsell conversion rate with professional reports.',
      price: '19.99'
    },
    {
      id: 'diagnostics',
      title: 'This Troubleshooting Method Cut Training Time in Half for New Technicians',
      cta: 'Explore Their Toolkit',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fdiagnostics-card.jpg?alt=media',
      description: 'Learn the advanced diagnostic system that reduced technician training time by 50% while improving first-visit resolution rates.',
      price: '19.99'
    },
    {
      id: 'reviews',
      title: 'The 5-Minute Routine That Generated 143 New 5-Star Reviews in One Month',
      cta: 'See Review System',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Freviews-card.jpg?alt=media',
      description: 'Discover the simple 5-minute process that helped this business collect 143 positive reviews in just one month, boosting their online reputation.',
      price: '24.99'
    },
    {
      id: 'website',
      title: 'Local Contractor Beats National Chain with This Simple Website Strategy',
      cta: 'View Website Demo',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fwebsite-card.jpg?alt=media',
      description: 'See how a small local service contractor outranked major national chains using our website builder and lead generation strategy.',
      price: '39.99'
    },
    {
      id: 'quickbooks',
      title: 'How This Owner Cut Bookkeeping Time From 20 Hours to Just 2 per Week',
      cta: 'See The Integration',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fquickbooks-card.jpg?alt=media',
      description: 'Learn how our QuickBooks integration reduced accounting workload from 20 hours to just 2 hours weekly while improving financial accuracy.',
      price: '29.99'
    },
    {
      id: 'customers',
      title: 'The Customer Insights That Increased Lifetime Value by 78%',
      cta: 'View CRM Demo',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fcustomers-card.jpg?alt=media',
      description: 'Discover the customer management system that revealed insights leading to a 78% increase in customer lifetime value and retention.',
      price: '19.99'
    },
    {
      id: 'leads',
      title: 'How a Small Plumbing Company Doubled Their Lead Conversion Rate in 30 Days',
      cta: 'See Lead System',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fleads-card.jpg?alt=media',
      description: 'Learn the lead tracking and follow-up system that doubled conversion rates for this small plumbing business within just 30 days.',
      price: '24.99'
    },
    {
      id: 'payments',
      title: 'The Payment Method That Reduced Past-Due Invoices by 94%',
      cta: 'Try Payment System',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fpayments-card.jpg?alt=media',
      description: 'See how our digital payment processing system virtually eliminated past-due invoices, reducing them by 94% and improving cash flow.',
      price: '19.99'
    },
    {
      id: 'estimates',
      title: 'This Estimate Template Increased Close Rates From 40% to 75%',
      cta: 'View Estimate Tool',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Festimates-card.jpg?alt=media',
      description: 'Discover the professional estimate template that nearly doubled sales close rates from 40% to 75% with better presentation and options.',
      price: '19.99'
    },
    {
      id: 'serviceagreement',
      title: 'How This Company Built a $1.2M Recurring Revenue Stream in 18 Months',
      cta: 'See Agreement System',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fservice-agreements-card.jpg?alt=media',
      description: 'Learn how this home service business created a stable $1.2M annual recurring revenue stream using our service agreement management system.',
      price: '19.99'
    },
    {
      id: 'discounts',
      title: 'The Counter-Intuitive Discount Strategy That Increased Average Sale by 28%',
      cta: 'See Discount Tools',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Fdiscounts-card.jpg?alt=media',
      description: 'Discover the surprising discount structure that actually increased average sale amounts by 28% while improving customer satisfaction.',
      price: '19.99'
    },
    {
      id: 'taxes',
      title: 'How This Multi-State Contractor Saved $37,000 in Tax Penalties',
      cta: 'View Tax System',
      image: 'https://firebasestorage.googleapis.com/v0/b/servicecrm-52868.appspot.com/o/feature-images%2Ftaxes-card.jpg?alt=media',
      description: 'See how our multi-jurisdiction tax calculation system helped this contractor avoid $37,000 in tax penalties while ensuring compliance.',
      price: '19.99'
    }
  ];

  const features = [
    {
      title: 'Websites, Funnels, and Business Integration',
      description: 'Seamlessly connect with QuickBooks, Google My Business, WiseTack, and other essential business platforms',
      image: website,
      price: '$99.95',
      stripeLink: 'https://buy.stripe.com/5kA3fLcwadvg9Ta5kk',
      available: true
    },
    {
      title: 'Pricebook, Load Calculations, and Inspection Tools',
      description: 'Access professional tools including pricebook management, Manual J calculations, and inspection systems',
      image: pricebook,
      price: '$99.95',
      stripeLink: 'https://buy.stripe.com/5kA3fLcwadvg9Ta5kk',
      available: true
    },
    {
      title: 'Team Management',
      description: 'Efficiently manage technicians, assign jobs, and track performance across your entire organization',
      image: techspage,
      price: '$99.95',
      stripeLink: 'https://buy.stripe.com/5kA3fLcwadvg9Ta5kk',
      available: true
    },
    {
      title: 'Core Operations, Job Management, Invoices, and Estimates',
      description: 'Create and manage invoices, estimates, and jobs with our comprehensive suite of business tools',
      image: jobs,
      price: '$99.95',
      stripeLink: 'https://buy.stripe.com/5kA3fLcwadvg9Ta5kk',
      available: true
    },  
    {
      title: 'Appointment Management, Leads Funnel, and Customer Details',
      description: 'Track customer information, manage appointments, and visualize work progress with our Status Board',
      image: leadsfunnel,
      price: '$99.95',
      stripeLink: 'https://buy.stripe.com/5kA3fLcwadvg9Ta5kk',
      available: true
    },
    {
      title: 'Multi-Industry Support',
      description: 'Specialized features for HVAC, plumbing, electrical, roofing, and general contracting businesses',
      image: pricebook,
      price: '$99.95',
      stripeLink: 'https://buy.stripe.com/5kA3fLcwadvg9Ta5kk',
      available: true
    }
  ];

  const featureImages = {
    'Core Operations, Job Management, Invoices, and Estimates': [jobs, invoices, leadsfunnel],
    'Appointment Management, Leads Funnel, and Customer Details': [kanbanboard, leadsfunnel, customerdetails],
    'Pricebook, Load Calculations, and Inspection Tools': [pricebook, pricebookedit, loadcalc, roombyroomloadcalc],
    'Websites, Funnels, and Business Integration': [website, chatbot],
    'Multi-Industry Support': [pricebook],
    'Team Management': [techspage]
  };
  
  const testimonials = [
    {
      quote: "Fixetra has transformed our business operations. We've reduced our paperwork by 80% and increased tech productivity by 30%.",
      author: "Michael Thompson",
      position: "Owner, Thompson HVAC Services",
      company: "Thompson HVAC"
    },
    {
      quote: "The scheduling and dispatch features alone were worth the investment. Our response times are down and customer satisfaction is up.",
      author: "Sarah Reynolds",
      position: "Operations Manager",
      company: "Reynolds Plumbing"
    },
    {
      quote: "As a small electrical contractor, Fixetra gives us the tools to compete with larger companies. The ROI has been incredible.",
      author: "James Martinez",
      position: "Founder",
      company: "Martinez Electrical"
    }
  ];

  const handleDemoFeature = (featureId) => {
    if (featureId === 'blog') {
      navigate('/blog');
    } else {
      navigate(`/features/${featureId}`);
    }
  };

  return (
    <div className="home">
      <Header />
      <main className="main">
        <section className="hero">
          <div className={`hero-background ${isHeroImageLoaded ? 'loaded' : ''}`} />
          <div className="hero-content">
            <h1 className="hero-title">Complete Home Services Business Management</h1>
            <p className="hero-description">All-in-one platform for HVAC, plumbing, and electrical contractors to manage jobs, technicians, customers, and grow your business.</p>
            <div className="hero-actions">
              <Link to="/features" className="button button-primary">
                Subscribe Now
              </Link>
            </div>
            <div className="social-proof">
              <div className="social-proof-logos">
                <img src={socialProofing} alt="Social Proofing" className="social-logo" />
              </div>
              <p className="social-proof-text">Trusted by leading platforms</p>
            </div>
          </div>
        </section>

        <section className="feature-showcase">
          <div className="section-container">
            <div className="section-header">
              <h2 className="section-title">Powerful Features for Home Service Businesses</h2>
              <p className="section-description">
                Choose only the features you need. Pay for what you use, and unlock more as your business grows.
              </p>
              <Link to="/features" className="button button-primary feature-explore-button">
                Explore All Features
              </Link>
            </div>
          </div>
        </section>
        
        <section className="features" id="features">
          <div className="features-content">
            <h2 className="features-title">Real Results: How Home Service Businesses Transformed Their Operations</h2>
            <p className="features-subtitle">Click any case study to see the exact tools they used</p>
            <div className="features-grid">
              {allFeatures.map((feature) => (
                <div 
                  className="feature-square-card" 
                  key={feature.id}
                  onClick={() => handleDemoFeature(feature.id)}
                  onKeyDown={(e) => e.key === 'Enter' && handleDemoFeature(feature.id)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Read about ${feature.title}`}
                  itemScope
                  itemType="https://schema.org/Product"
                >
                  <meta itemProp="sku" content={feature.id} />
                  <meta itemProp="image" content={feature.image} />
                  <meta itemProp="brand" content="Fixetra" />
                  <meta itemProp="description" content={feature.description} />
                  <div itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
                    <meta itemProp="ratingValue" content="4.6" />
                    <meta itemProp="reviewCount" content="89" />
                  </div>
                  <div className="feature-card-content">
                    <h3 className="feature-card-title" itemProp="name">{feature.title}</h3>
                    <div itemProp="offers" itemScope itemType="https://schema.org/Offer">
                      <meta itemProp="price" content={feature.price} />
                      <meta itemProp="priceCurrency" content="USD" />
                      <meta itemProp="availability" content="https://schema.org/InStock" />
                      <meta itemProp="url" content={`${window.location.origin}/features/${feature.id}`} />
                      <button 
                        className="button button-primary"
                        aria-hidden="true"
                      >
                        {feature.cta}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <section className="testimonials" id="testimonials">
          <div className="testimonials-content">
            <h2 className="testimonials-title">What Our Customers Say</h2>
            <div className="testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div className="testimonial-card" key={index}>
                  <div className="testimonial-quote">
                    <p>"{testimonial.quote}"</p>
                  </div>
                  <div className="testimonial-author">
                    <p className="author-name">{testimonial.author}</p>
                    <p className="author-position">{testimonial.position}, {testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cta" id="demo">
          <div className="cta-content">
            <div className="cta-center">
              <h2 className="cta-title">Ready to Transform Your Service Business?</h2>
              <p className="cta-description">
                Join thousands of home service professionals who are already streamlining their operations with Fixetra.
              </p>
              <div className="cta-form-container">
                <Link to="/features" className="button button-primary">
                  Subscribe Now
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Fixetra Pricebook Pro",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": 4.6,
            "ratingCount": 648
          },
          "offers": {
            "@type": "Offer",
            "price": "99.95",
            "priceCurrency": "USD"
          },
          "description": "Professional HVAC and home services management pricebook software"
        })}
      </script>

      {/* Product structured data for Google SEO - Feature cards */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": allFeatures.map((feature, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Product",
              "name": feature.title,
              "description": feature.description,
              "image": feature.image,
              "sku": feature.id,
              "mpn": `fixetra-${feature.id}`,
              "brand": {
                "@type": "Brand",
                "name": "Fixetra"
              },
              "offers": {
                "@type": "Offer",
                "url": `${window.location.origin}/features/${feature.id}`,
                "priceCurrency": "USD",
                "price": feature.price,
                "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
                "availability": "https://schema.org/InStock",
                "itemCondition": "https://schema.org/NewCondition",
                "seller": {
                  "@type": "Organization",
                  "name": "Fixetra"
                }
              },
              "review": {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "4.8",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Home Service Business Owner"
                }
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.6",
                "reviewCount": "89",
                "bestRating": "5"
              }
            }
          }))
        })}
      </script>

      {/* Individual product markup for each case study feature */}
      {allFeatures.map(feature => (
        <script key={feature.id} type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": feature.title,
            "description": feature.description,
            "image": feature.image,
            "sku": feature.id,
            "mpn": `fixetra-${feature.id}`,
            "brand": {
              "@type": "Brand",
              "name": "Fixetra"
            },
            "offers": {
              "@type": "Offer",
              "url": `${window.location.origin}/features/${feature.id}`,
              "priceCurrency": "USD",
              "price": feature.price,
              "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
              "availability": "https://schema.org/InStock",
              "itemCondition": "https://schema.org/NewCondition",
              "seller": {
                "@type": "Organization",
                "name": "Fixetra"
              }
            },
            "review": {
              "@type": "Review",
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": "4.8",
                "bestRating": "5"
              },
              "author": {
                "@type": "Person",
                "name": "Home Service Business Owner"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.6",
              "reviewCount": "89",
              "bestRating": "5"
            }
          })}
        </script>
      ))}
    </div>
  );
};

export default Home;
